import type { SubscriptionTier } from '@/types/dtos/transactions/subscription-plan.dto';

/** Upper bound for numeric bullet keys under `managePlanFeatureLists.*.*.<n>`. */
const MANAGE_PLAN_I18N_FEATURE_BULLET_MAX = 50;

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * Marketing bullets from `managePlanFeatureLists.<productKey>.<tier>.<n>`.
 * Gaps in numbering are fine — missing keys are skipped.
 */
export function getManagePlanFeatureListBullets(
  t: Translate,
  productKey: string,
  tier: SubscriptionTier | string,
): string[] {
  const pk = productKey.trim();
  if (!pk) return [];
  const base = `subscription.organization.planPage.managePlanFeatureLists.${pk}.${tier}`;
  const lines: string[] = [];
  for (let i = 0; i < MANAGE_PLAN_I18N_FEATURE_BULLET_MAX; i++) {
    const key = `${base}.${i}`;
    const value = t(key);
    if (value !== key) {
      lines.push(value);
    }
  }
  return lines;
}

/** Lowest → highest ladder used when stacking feature-list bullets for a chosen tier. */
const MANAGE_PLAN_FEATURE_LIST_TIER_LADDER = ['FREE', 'BASIC', 'SMART', 'PRO', 'PREMIUM'] as const;

/**
 * Feature-list bullets from the lowest tier through `tier` (inclusive), in ladder order.
 * Tiers without `managePlanFeatureLists` entries contribute nothing.
 */
export function getManagePlanFeatureListBulletsUpToTier(
  t: Translate,
  productKey: string,
  tier: SubscriptionTier | string,
): string[] {
  const target = String(tier ?? '').trim();
  if (!target) return [];
  const idx = MANAGE_PLAN_FEATURE_LIST_TIER_LADDER.indexOf(
    target as (typeof MANAGE_PLAN_FEATURE_LIST_TIER_LADDER)[number],
  );
  const ladder = idx >= 0 ? MANAGE_PLAN_FEATURE_LIST_TIER_LADDER.slice(0, idx + 1) : ([target] as readonly string[]);
  const lines: string[] = [];
  for (const step of ladder) {
    lines.push(...getManagePlanFeatureListBullets(t, productKey, step));
  }
  return lines;
}

/**
 * Pluralized label from `planQuotaKey` only (no raw-key fallback).
 * Tries namespaced path (`host_default:members` → `planQuotaKey.host_default.members`), then last segment.
 * Pass `count` so i18next resolves `_one` / `_other`.
 */
export function resolvePlanQuotaKeyLabel(t: Translate, rawQuotaKey: string, count = 1): string | undefined {
  const quotaKey = String(rawQuotaKey ?? '').trim();
  if (!quotaKey) return undefined;
  const segments = quotaKey
    .split(':')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!segments.length) return undefined;

  const dotted = `subscription.organization.planPage.planQuotaKey.${segments.join('.')}`;
  const fromPath = t(dotted, { count });
  if (fromPath !== dotted) return fromPath;

  const last = segments[segments.length - 1]!;
  const suffixKey = `subscription.organization.planPage.planQuotaKey.${last}`;
  const fromSuffix = t(suffixKey, { count });
  return fromSuffix !== suffixKey ? fromSuffix : undefined;
}

/** Label for a commercial feature key from `planFeatureKey` only (no raw-key fallback). */
export function resolvePlanFeatureKeyLabel(t: Translate, featureKey: string): string | undefined {
  const key = String(featureKey ?? '').trim();
  if (!key) return undefined;
  const i18nKey = `subscription.organization.planPage.planFeatureKey.${key}`;
  const resolved = t(i18nKey);
  return resolved !== i18nKey ? resolved : undefined;
}

export type ManagePlanIncludedQuotaRow = { quotaKey: string; line: string };

/** Included quota lines: `"{{quantity}} {{planQuotaKey label}} {{included}}"` when a translation exists. */
export function resolveIncludedQuotaDisplayRows(
  t: Translate,
  includedQuotas: { quotaKey: string; quantity: number }[] | undefined,
  previousIncludedQuotas?: { quotaKey: string; quantity: number }[],
): ManagePlanIncludedQuotaRow[] {
  const rows: ManagePlanIncludedQuotaRow[] = [];
  const included = t('subscription.organization.planPage.included');
  for (const q of includedQuotas ?? []) {
    const quotaKey = String(q.quotaKey ?? '').trim();
    if (!quotaKey) continue;
    const quantity = typeof q.quantity === 'number' && Number.isFinite(q.quantity) ? q.quantity : 0;
    const label = resolvePlanQuotaKeyLabel(t, quotaKey, quantity);
    if (!label) continue;
    const previousQuantity =
      previousIncludedQuotas?.find(
        (previous) =>
          String(previous.quotaKey ?? '')
            .trim()
            .toLowerCase() === quotaKey.toLowerCase(),
      )?.quantity ?? 0;
    if (quantity <= previousQuantity) continue;
    rows.push({ quotaKey, line: `${quantity} ${label} ${included}` });
  }
  return rows;
}

export type ManagePlanIncludedFeatureRow = { featureKey: string; line: string };

/** Included commercial feature lines when a `planFeatureKey` translation exists. */
export function resolveIncludedFeatureKeyDisplayRows(
  t: Translate,
  featureKeys: string[] | undefined,
  previousFeatureKeys?: string[],
): ManagePlanIncludedFeatureRow[] {
  const previousKeys = new Set((previousFeatureKeys ?? []).map((key) => String(key).trim().toLowerCase()));
  const rows: ManagePlanIncludedFeatureRow[] = [];
  for (const raw of featureKeys ?? []) {
    const featureKey = String(raw ?? '').trim();
    if (!featureKey) continue;
    if (previousKeys.has(featureKey.toLowerCase())) continue;
    const label = resolvePlanFeatureKeyLabel(t, featureKey);
    if (!label) continue;
    rows.push({ featureKey, line: label });
  }
  return rows;
}
